const DRAFT_STORAGE_PREFIX = "hub-chat-drafts:v1:";

interface StoredDrafts {
  selectedAgentId?: string;
  drafts: Record<string, string>;
}

export interface DraftSnapshot {
  selectedAgentId?: string;
  text: string;
  storageAvailable: boolean;
}

function storageKey(userId: string): string {
  return `${DRAFT_STORAGE_PREFIX}${encodeURIComponent(userId)}`;
}

function readStoredDrafts(userId: string): { value: StoredDrafts; storageAvailable: boolean } {
  let storage: Storage;
  let raw: string | null;
  try {
    const availableStorage = globalThis.sessionStorage;
    if (!availableStorage) return { value: { drafts: {} }, storageAvailable: false };
    storage = availableStorage;
    raw = storage.getItem(storageKey(userId));
  } catch {
    return { value: { drafts: {} }, storageAvailable: false };
  }
  if (!raw) return { value: { drafts: {} }, storageAvailable: true };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return { value: { drafts: {} }, storageAvailable: true };
    }
    const candidate = parsed as { selectedAgentId?: unknown; drafts?: unknown };
    const drafts =
      candidate.drafts && typeof candidate.drafts === "object"
        ? Object.fromEntries(
            Object.entries(candidate.drafts).filter(
              (entry): entry is [string, string] => typeof entry[1] === "string",
            ),
          )
        : {};
    return {
      value: {
        drafts,
        ...(typeof candidate.selectedAgentId === "string"
          ? { selectedAgentId: candidate.selectedAgentId }
          : {}),
      },
      storageAvailable: true,
    };
  } catch {
    try {
      storage.removeItem(storageKey(userId));
      return { value: { drafts: {} }, storageAvailable: true };
    } catch {
      return { value: { drafts: {} }, storageAvailable: false };
    }
  }
}

function writeStoredDrafts(userId: string, value: StoredDrafts): boolean {
  try {
    const storage = globalThis.sessionStorage;
    if (!storage) return false;
    storage.setItem(storageKey(userId), JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function newTaskDraftId(agentId?: string): string {
  return agentId ? `new:${agentId}` : "new:unassigned";
}

export function conversationDraftId(agentId: string, sessionId: string): string {
  return `conversation:${agentId}:${sessionId}`;
}

export function readDraft(userId: string, draftId: string): DraftSnapshot {
  const stored = readStoredDrafts(userId);
  return {
    selectedAgentId: stored.value.selectedAgentId,
    text: stored.value.drafts[draftId] ?? "",
    storageAvailable: stored.storageAvailable,
  };
}

export function saveDraft(userId: string, draftId: string, text: string): boolean {
  const stored = readStoredDrafts(userId);
  if (!stored.storageAvailable) return false;
  if (text) stored.value.drafts[draftId] = text;
  else delete stored.value.drafts[draftId];
  return writeStoredDrafts(userId, stored.value);
}

export function clearDraftIfMatches(
  userId: string,
  draftId: string,
  submittedText: string,
): boolean {
  const stored = readStoredDrafts(userId);
  if (!stored.storageAvailable) return false;
  if (stored.value.drafts[draftId] !== submittedText) return true;
  delete stored.value.drafts[draftId];
  return writeStoredDrafts(userId, stored.value);
}

export function saveSelectedAgent(userId: string, agentId: string): boolean {
  const stored = readStoredDrafts(userId);
  if (!stored.storageAvailable) return false;
  if (agentId) stored.value.selectedAgentId = agentId;
  else delete stored.value.selectedAgentId;
  return writeStoredDrafts(userId, stored.value);
}

export function clearUserDrafts(userId: string): void {
  try {
    globalThis.sessionStorage?.removeItem(storageKey(userId));
  } catch {
    // Signing out must continue when browser storage is unavailable.
  }
}
